import { Transaction } from "@iterate/common/types/shared";
import * as React from "react";
import { Button, FloatingLabel, Form, Modal } from "react-bootstrap";
import { CategorySelect } from "src/components/CategorySelect";
import { trpc } from "src/trpc";
import { useBackButton } from "src/hooks/useBackButton";

export interface Props {
  transaction: Transaction;
  onClose(): void;
}

interface Form {
  categoryId: string | null;
  description: string;
  amount: number;
}

export function SplitTransactionForm({ transaction, onClose }: Props) {
  useBackButton(onClose);
  const utils = trpc.useUtils();
  const { mutateAsync: splitTransaction } = trpc.splitTransaction.useMutation();
  const [amountStr, setAmountStr] = React.useState(transaction.amount.toFixed(2));
  const [form, setForm] = React.useState<Form>({
    categoryId: transaction.categoryId,
    description: transaction.description,
    amount: parseFloat(amountStr),
  });

  const setField = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm({ ...form, [key]: value });

  const setAmount = (amountStr: string) => {
    setAmountStr(amountStr);
    const amount = parseFloat(amountStr);
    setField("amount", amount);
  };

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    await splitTransaction({
      transactionId: transaction.id,
      ...form,
    });
    await utils.getTransactions.invalidate();
    await utils.getAggregateSpend.invalidate();
    onClose();
  };

  const isDebit = transaction.amount < 0;

  return (
    <Modal show onHide={onClose}>
      <Form onSubmit={onSave}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <CategorySelect
              value={form.categoryId}
              onChange={(catId) => setField("categoryId", catId)}
            />
          </Form.Group>
          <FloatingLabel label="Description" className="mb-3">
            <Form.Control
              value={form.description ?? ""}
              onChange={(e) => setField("description", e.target.value)}
            />
          </FloatingLabel>
          <FloatingLabel label="Amount" className="mb-3">
            <Form.Control
              value={amountStr}
              type="number"
              max={isDebit ? 0 : transaction.amount}
              min={isDebit ? transaction.amount : 0}
              step={0.01}
              onChange={(e) => setAmount(e.target.value)}
            />
          </FloatingLabel>
        </Modal.Body>
        <Modal.Footer>
          <Button type="submit" disabled={isNaN(form.amount)}>
            Split Transaction
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
